import { inspect } from "util";

import {
  isNodeEnvironment,
  serializeError,
} from "../__internal__/logging/utils";
import { LogMetadataKey } from "../LogMetadataKey";
import { Tracer } from "../tracing";

import { Attributes, LoggingContext, LoggingHandler } from "./types";

export const nodeLoggingHandler: LoggingHandler = (context) => {
  if (!isNodeEnvironment()) {
    throw new Error(
      "nodeLogHandler should only be used in a node.js environment."
    );
  }
  let logString: string;

  if (process.env.ENVIRONMENT && process.env.ENVIRONMENT !== "local") {
    logString = datadogAgentHandler(context);
  } else {
    logString = localTerminalHandler(context);
  }

  // Prefer stdio over console — some apps override console methods.
  // Access via globalThis so Edge static analysis does not flag process.stdout/stderr
  // when this Node-only module is pulled into Middleware/Edge instrumentation graphs.
  const useStderrOnly =
    context.level === "error" ||
    context.level === "warn" ||
    context.level === "fatal";

  writeToNodeStdio(logString, useStderrOnly);
};

const writeToNodeStdio = (logString: string, useStderrOnly: boolean): void => {
  const nodeProcess = (
    globalThis as {
      process?: {
        stdout?: { write: (chunk: string) => boolean };
        stderr?: { write: (chunk: string) => boolean };
      };
    }
  ).process;
  const stream = useStderrOnly ? nodeProcess?.stderr : nodeProcess?.stdout;
  // Silently no-op if stdio is missing — console fallback can recurse because
  // apps override console.error to call Logger.
  stream?.write?.(logString);
};

const localTerminalHandler: LogStringHandler = ({
  message,
  attributes,
  error,
}) => {
  let logString: string;
  if (error?.argument) {
    attributes ??= {};
    attributes.error = serializeError(error.instance);
  }
  try {
    logString = `${message}\n`;
    Object.entries(attributes ?? {}).forEach(([key, value]) => {
      logString += ` - ${key}: ${inspect(value, { colors: true, compact: false, depth: null }).replace(/\n/g, "\n   ")}\n`;
    });
  } catch {
    // In the case of inspect throwing, preserve what we can.
    attributes = safeSerializeAttributes(attributes);
    logString = `[Error serializing attributes]: ${message} ${JSON.stringify(attributes)}\n`;
  }
  return logString;
};

const datadogAgentHandler: LogStringHandler = ({
  level,
  message,
  attributes,
  error,
  productArea,
  pod,
  pillar,
  oncall,
}) => {
  let logString: string;
  const traceContext = Tracer.getActiveSpanContext();

  const data: Attributes = {
    level,
    message,
    timestamp: new Date(),
  };

  attributes ??= {};

  if ((level === "warn" || level === "error" || level === "fatal") && error) {
    attributes.error = serializeError(error.instance);
  }

  attributes = {
    [LogMetadataKey.FAIRE_PRODUCT_AREA]: productArea,
    [LogMetadataKey.FAIRE_POD]: pod,
    [LogMetadataKey.FAIRE_PILLAR]: pillar,
    [LogMetadataKey.FAIRE_ONCALL]: oncall,
    "dd.trace_id": traceContext?.traceId,
    "dd.span_id": traceContext?.spanId,
    "dd.trace_flags": traceContext?.traceFlags,
    ...attributes,
  };

  try {
    logString = JSON.stringify({ ...attributes, ...data }) + "\n";
  } catch {
    // In the case of JSON.stringify throwing (e.g. due to circular references),
    // preserve attributes that are individually serializable.
    data.message = `[Error serializing attributes]: ${data.message}`;
    attributes = safeSerializeAttributes(attributes);
    logString = JSON.stringify({ ...attributes, ...data }) + "\n";
  }
  return logString;
};

/**
 * Try to preserve each attribute individually; only replace values that
 * cannot be serialized (e.g. circular references) with a descriptive string.
 */
const safeSerializeAttributes = (
  attributes: Attributes | undefined
): Attributes => {
  if (!attributes) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(attributes).map(([key, value]) => {
      try {
        JSON.stringify(value);
        return [key, value];
      } catch {
        if (typeof value === "object" && value !== null) {
          const name = value.constructor?.name ?? "Object";
          return [key, `[Unserializable ${name}]`];
        }
        return [key, String(value)];
      }
    })
  );
};

type LogStringHandler = (context: LoggingContext) => string;
